<template>
  <ResponseTile
    v-if="showErrorTile"
    :back-button-label="$t('requestAccompaniedDrivingList.response.buttonLabel')"
    :text="$t('requestAccompaniedDrivingList.response.serviceErrorText')"
    :title="$t('requestAccompaniedDrivingList.response.serviceErrorTitle')"
    @button:back="formAbortActionToServicePage"
  ></ResponseTile>
  <Card
    v-else-if="showNoContractsTile"
    :footerDivider="false"
    :loadingState="false"
    :title="errorTitle"
  >
    <template #content>
      <span v-html="errorText"></span>
    </template>
    <template #footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          :buttonColor="ButtonColor.SECONDARY"
          @click="goToOnlineFormularLink()"
        >
          {{ $t('requestAccompaniedDrivingList.error.goToOnlineFormularLink') }}
        </CustomButton>
        <CustomButton
          :buttonColor="ButtonColor.LINK"
          @click="formAbortActionToServicePage()"
        >
          {{ errorButtonLabel }}
        </CustomButton>
      </div>
    </template>
  </Card>
  <div v-else>
    <Spinner v-if="formLoading" class="fix-spinner"></Spinner>
    <div v-else>
      <SelfServiceTile
        :apiRequestConfig="apiRequestConfig"
        :changeRequestType="changeRequestType()"
        :checkPoliciesDetails="checkPoliciesDetails"
        :checkPolicyDetails="checkPolicyDetails"
        :checkPolicyOverview="checkPolicyOverview"
        :formAbortAction="formAbortActionToServicePage"
        :formLoading="formLoading"
        :formShowResponse="formShowResponse"
        :formSubmitDisabled="formSubmitDisabled"
        :formSubmitting="formSubmitting"
        :getFormBlocks="getFormBlocks"
        :getListBlocks="getListBlocks"
        :modifyPolicy="modifyPolicy"
        :multiSelect="false"
        :preSelectedPolicyIds="preSelectedPolicyIds"
        :selfServiceLabels="selfServiceLabels"
        :setSelectedPolicies="setSelectedPolicies"
        :showDateOption="false"
        :staticPoliciesWithIconAndTitles="staticPoliciesFiltered"
        @formInputStartDate="setFormInputStartDate"
        @formSubmit="formSubmit"
      >
        <template #change-component>
          <RequestAccompaniedDrivingForm
            ref="form"
            :formStartDate="formStartDate"
            :policies="selectedPolicies as any"
            @formLoading="setFormLoading"
            @formResetButton="resetFormButton"
            @formSubmitDisabled="setFormSubmitDisabled"
            @showErrorTile="setShowErrorTile"
          ></RequestAccompaniedDrivingForm>
        </template>
        <template #header-subline>
          <p>
            {{ $t('requestAccompaniedDrivingList.header.ratgeberPrefix1') }}
            <br />
            {{ $t('requestAccompaniedDrivingList.header.ratgeberPrefix2') }}
            <a :href="ratgeberLink" target="_blank">{{
              $t('requestAccompaniedDrivingList.header.ratgeberLink')
            }}</a>
            {{ $t('requestAccompaniedDrivingList.header.ratgeberSuffix') }}
          </p>
          <br />
        </template>
      </SelfServiceTile>
      <br />
      <p class="self-service-selection-headline">
        {{ $t('requestAccompaniedDrivingList.footer.title') }}
      </p>
      {{ $t('requestAccompaniedDrivingList.footer.accompaniedDrivingRequestText') }}
      <br />
      {{ $t('requestAccompaniedDrivingList.footer.accompaniedDrivingRequestText2') }}
      <a :href="onlineFormularLink" target="_blank"
        >{{ $t('requestAccompaniedDrivingList.footer.onlineFormularLink') }} </a
      >.
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, type Ref, ref } from 'vue'
import {
  ButtonColor,
  Card,
  CustomButton,
  type KeyValueType,
  type PolicyWithIconAndTitles,
  ResponseTile,
  type SelfServiceLabels,
  SelfServiceTile,
  Spinner
} from 'de-zurich-ui-library'
import RequestAccompaniedDrivingForm from '@/components/policy/self-services/RequestAccompaniedDrivingForm.vue'
import {
  goToOnlineFormularLink,
  useSelfServicePolicyList
} from '@/composables/useSelfServicePolicyList'
import { selfServiceListAutoOnlyOnMounted } from '@/utils/policy/PolicyUtils'
import { useI18n } from 'vue-i18n'
import { usePoliciesStore } from '@/stores/policiesStore'

const { t } = useI18n()

const policiesStore = usePoliciesStore()

const {
  apiRequestConfig,
  checkPoliciesDetails,
  checkPolicyDetails,
  checkPolicyOverview,
  formAbortActionToServicePage,
  formLoading,
  loadPolicies,
  modifyPolicy,
  selectedPolicies,
  setFormLoading,
  setSelectedPolicies,
  setShowErrorTile,
  showErrorTile,
  staticPoliciesFiltered
} = useSelfServicePolicyList(selfServiceListAutoOnlyOnMounted)

// authorization and redirects
const form = ref<typeof RequestAccompaniedDrivingForm | null>(null)
const formSubmitting = ref(false)
const formSubmitDisabled = ref(true)
const formShowResponse = ref(false)
const formStartDate = ref('')
const showNoContractsTile = ref(false)
const selfServiceLabels: SelfServiceLabels = {
  errorText: t('requestAccompaniedDrivingList.error.errorText').toString(),
  errorTitle: t('requestAccompaniedDrivingList.error.errorTitle').toString(),
  formCardTitle: t('requestAccompaniedDrivingList.form.formCardTitle').toString(),
  formCurrentDataLabel: t(
    'requestAccompaniedDrivingList.form.formCurrentDataLabel'
  ).toString(),
  formCurrentDataValue: '',
  formDateErrorText: t('requestAccompaniedDrivingList.form.formDateErrorText').toString(),
  formDateLabel: t('requestAccompaniedDrivingList.form.formInputLabelDate').toString(),
  formHintLabel: t('requestAccompaniedDrivingList.form.formHintLabel').toString(),
  formSubmitButtonLabel: t(
    'requestAccompaniedDrivingList.form.formSubmitButtonLabel'
  ).toString(),
  listHeadline: t('requestAccompaniedDrivingList.list.listHeadline').toString(),
  responseText: t('requestAccompaniedDrivingList.response.thankYouText').toString(),
  responseTileBackButtonLabel: t(
    'requestAccompaniedDrivingList.response.buttonLabel'
  ).toString(),
  responseTitle: t('requestAccompaniedDrivingList.response.thankYouTitle').toString()
}
const preSelectedPolicyIds: Ref<string[]> = ref([])

const ratgeberLink: Ref<string> = ref(
  globalThis.ENV_CONFIG?.REDIRECTS?.ACCOMPANIED_DRIVING_RATGEBER ?? ''
)

const onlineFormularLink: Ref<string> = ref(
  globalThis.ENV_CONFIG?.REDIRECTS?.ACCOMPANIED_DRIVING_FORMULAR ?? ''
)

const errorTitle = ref('')
const errorText = ref('')
const errorButtonLabel = ref('')

enum ChangeRequestType {
  AccompaniedDrivingAnfordern = 'AccompaniedDrivingAnfordern'
}

onMounted(async () => {
  // Preselection: consume all policy ids once for the self-service list
  preSelectedPolicyIds.value = policiesStore.consumePreSelectedPolicyIds()

  const filtered = await loadPolicies()
  if (filtered.length === 0) {
    setErrorTileNoContracts()
  }
  setFormLoading(false)
})

function setErrorTileNoContracts() {
  errorTitle.value = t('requestAccompaniedDrivingList.error.errorTitle')
  errorText.value = t('requestAccompaniedDrivingList.error.errorText')
  errorButtonLabel.value = t('requestAccompaniedDrivingList.error.buttonLabel')
  setShowNoContractsTile(true)
}

function changeRequestType(): ChangeRequestType {
  return ChangeRequestType.AccompaniedDrivingAnfordern
}

// Default Methods

function setFormSubmitDisabled(value: boolean): void {
  formSubmitDisabled.value = value
}

function setShowNoContractsTile(value: boolean): void {
  showNoContractsTile.value = value
}

function setFormInputStartDate(value: string): void {
  formStartDate.value = value
}

function resetFormButton(): void {
  formSubmitting.value = false
}

function formSubmit(): void {
  formSubmitting.value = true
  form.value?.formSubmit()
}

function getListBlocks(): KeyValueType[] {
  return []
}

function getFormBlocks(policies: any): KeyValueType[][] {
  const blocks = []
  for (const policy of policies as PolicyWithIconAndTitles[]) {
    const block: KeyValueType[] = []
    // only licensePlate and policyNumber
    block.push({
      key: policy.secondTitle ?? '',
      value: 'Versicherungsnummer ' + policy.policyNumber,
      type: 'policy'
    })
    blocks.push(block)
  }
  return blocks
}
</script>

<style lang="scss" scoped>
.fix-spinner {
  position: relative !important;
  top: 64px !important;
}

.is-styled {
  .self-service-selection-headline {
    font-family: var(--sst-headline-font-family), serif;
    font-size: 20px;
    color: var(--sst-headline-color) !important;
    margin-top: 0;
    margin-bottom: calc(var(--baseline) * 2) !important;
  }
}
</style>
